// Self-contained client for the PROCUREMENT side of hardware-service — vendors,
// goods-received (GRN), damage/write-offs, and brand taxonomy.
//
// RECONCILED (wave W4b) against the REAL endpoints wave W2 shipped — the
// authoritative contract is `w2-report.md` in the overnight plan folder.
// Notable server facts this file now reflects:
//   - GRN routes are `/grn` (singular), fields are `poRef` / `qty` / `serial`.
//   - There is NO `GET /vendors/{id}` — vendor detail is composed client-side
//     from `GET /vendors` + `GET /grn?vendorId=`.
//   - Brands are NOT rows: `GET /brands` returns distinct `brand` strings from
//     models; a brand is created by setting `brand` on a model. No POST.
//   - Reports return the uniform `{ columns, rows, totals?, generatedAt }`
//     shape directly from `GET /reports/{reportId}` — no `{ items }` wrapper.
//
// EVERY read here is used with a try/catch by its caller and rendered as an
// empty/error state rather than a crash.

const detectStage = (): 'prod' | 'dev' | 'staging' => {
  try {
    const v = (import.meta as any)?.env?.VITE_STAGE;
    if (v === 'prod' || v === 'staging' || v === 'dev') return v;
  } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.startsWith('admin.dev.') || h.includes('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage')) return 'staging';
  return 'prod';
};

/** Pure host resolver — same shape as `resolveHardwareHost` in hardwareApi.ts. */
export function resolveInventoryHost(hostname: string, stage: 'prod' | 'staging' | 'dev', envBase?: string): string {
  if (envBase) return envBase;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return '/hardware-api';
  const apex = hostname.replace(/^(dev|stage|www|admin\.dev|admin\.stage|admin)\./, '');
  if (stage === 'prod') return `https://platform.api.${apex}/hardware`;
  if (stage === 'staging') return `https://platform.api.stage.${apex}/hardware`;
  return `https://platform.api.dev.${apex}/hardware`;
}

const host = (): string => {
  let envBase: string | undefined;
  try { envBase = (import.meta as any)?.env?.VITE_HARDWARE_API_BASE || undefined; } catch { /* ignore */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  return resolveInventoryHost(h, detectStage(), envBase);
};

function authHeader(): Record<string, string> {
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('jwtToken')) || '';
  return token ? { Authorization: token } : {};
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${host()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', ...authHeader(), ...(init.headers || {}) },
  });
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = body?.error || '';
    } catch { /* non-JSON body — most likely from a route that doesn't exist yet */ }
    throw new Error(detail || `hardware ${res.status}`);
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

// ---------------------------------------------------------------------------
// Vendors — server shape from w2-report.md `GET /vendors`
// ---------------------------------------------------------------------------

/** A person at the vendor — deliberately just the basics. */
export interface VendorContact {
  name: string;
  role?: string;
  phone?: string;
  email?: string;
}

export interface VendorRow {
  vendorId: string;
  name: string;
  contacts?: VendorContact[];
  address?: string;
  phone?: string;             // main rep / office number
  website?: string;
  paymentTerms?: string;      // e.g. "net-30"
  leadTimeDays?: number;
  notes?: string;
  active: boolean;
  createdAt: number;
  updatedAt: number;
  /** Server-derived scorecard — null when no eligible GRN history exists. */
  grnCount?: number;
  onTimePct?: number | null;
  defectPct?: number | null;
  spendCents?: number;
}

// ---------------------------------------------------------------------------
// GRN — Goods Received Note (server shape from `GET /grn`)
// ---------------------------------------------------------------------------

/** What `POST /grn` accepts per line (max 24 lines per GRN). */
export interface GrnLineInput {
  modelId: string;
  qty: number;
  unitCostCents: number;
}

export interface GrnLineRow extends GrnLineInput {
  grnId: string;
  lineNo: number;
  receivedQty: number;
  damagedQty: number;
  remainingQty: number;
  createdAt: number;
  updatedAt: number;
}

export interface GrnRow {
  grnId: string;
  /**
   * Human-readable, sequential: GRN-2026-0001. Additive — every GRN created
   * before the server minted these has none, so treat it as optional forever
   * and render through `grnLabel()`, never raw.
   */
  grnNumber?: string;
  vendorId: string;
  poRef?: string;
  expectedAt?: number;
  status: 'draft' | 'in_transit' | 'receiving' | 'received';
  receivedAt?: number;
  createdAt: number;
  updatedAt: number;
  lines: GrnLineRow[];
  progress: { totalQty: number; receivedQty: number; damagedQty: number; remainingQty: number };
}

// ---------------------------------------------------------------------------
// Damage & write-offs — always answers "who pays"
// ---------------------------------------------------------------------------

/** Damage lifecycle wave — a report starts 'open' and is closed out via `resolveDamage`. Rows written before this wave carry no `status` — the server always reads/returns those as 'open', never backfilled. */
export type DamageStatus = 'open' | 'resolved' | 'cancelled';

/** What `POST /lifecycle/damage/resolve` accepts. `cancel` = "this report was a mistake"; the other four close it out with an outcome. */
export type DamageResolveAction = 'cancel' | 'vendor_credit' | 'returned_to_vendor' | 'repaired_to_stock' | 'written_off';

export interface DamageResolution {
  action: 'vendor_credit' | 'returned_to_vendor' | 'repaired_to_stock' | 'written_off' | 'cancelled';
  notes?: string;
  by: string;
  at: number;
}

export interface DamageRow {
  /**
   * Absent on a MODEL-SCOPED row (serialized-vs-quantity-tracking wave) — a
   * quantity-mode model has no unit to log damage against, so `modelId`+`qty`
   * stand in instead. Never resolvable through `resolveDamage` (that endpoint
   * moves a UNIT's state); render these read-only, no inline actions.
   */
  unitId?: string;
  /** Model-scoped rows only — the magnitude of stock lost. */
  qty?: number;
  /** Absent on a model-scoped row — those carry no unit, so no unit "stage". */
  stage?: string;              // 'receiving' | 'in_stock' | 'deployed' | ...
  reason: string;
  chargedTo: 'vendor' | 'tenant' | 'writeoff';
  amountCents?: number;
  notes?: string;
  vendorId?: string;
  grnId?: string;
  modelId?: string;
  actor?: string;
  actorEmail?: string;
  createdAt: number;
  /** Always present on the wire (server normalizes legacy rows to 'open'). */
  status: DamageStatus;
  /** Set only once the report is resolved or cancelled — see `resolveDamage`. */
  resolution?: DamageResolution;
}

// ---------------------------------------------------------------------------
// Stock movement ledger (serialized-vs-quantity-tracking wave) — every count
// change on a 'quantity' model is a signed ledger entry; on-hand/reserved are
// DERIVED sums, never a stored counter (mockup screen 4). Mirrors
// `StockMovementSchema` in `packages/shared-types/src/deviceOrders.ts`.
//
//   onHand    = Σ qty over {received, adjustment, packed, damaged}
//   reserved  = max(0, Σ qty over {reserved} − Σ qty over {released})
//   available = onHand − reserved
//
// Per-kind sign (enforced server-side, never re-derived here):
//   received +, adjustment ± (caller's sign), packed −, damaged −, reserved +, released +.
// ---------------------------------------------------------------------------

export type StockMovementKind = 'received' | 'adjustment' | 'reserved' | 'released' | 'packed' | 'damaged';

export interface StockMovementRow {
  modelId: string;
  at: number;
  kind: StockMovementKind;
  qty: number;
  reason?: string;
  note?: string;
  grnId?: string;
  orderId?: string;
  boxNo?: number;
  unitCostCents?: number;
  actor?: string;
}

export interface StockSummary { onHand: number; reserved: number; available: number }

/** What `POST /lifecycle/stock/{modelId}/adjust` accepts. `'other'` requires `note`; `'damaged'` requires a negative `delta` and also writes a model-scoped `DamageRow`. */
export type StockAdjustReason = 'recount' | 'damaged' | 'consumed' | 'found' | 'other';

export const DAMAGE_STATUS_CHIP: Record<DamageStatus, string> = {
  open: 'bg-amber-100 text-amber-700 border-amber-200',
  resolved: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  cancelled: 'bg-stone-100 text-stone-500 border-stone-200',
};
export const DAMAGE_STATUS_LABEL: Record<DamageStatus, string> = { open: 'Open', resolved: 'Resolved', cancelled: 'Cancelled' };

// ---------------------------------------------------------------------------
// Reports — the uniform wire shape `GET /reports/{reportId}` returns for
// every report, present and future.
// ---------------------------------------------------------------------------

export interface ReportWire {
  columns: { key: string; label: string; format?: 'money' | 'number' | 'percent' | 'date' }[];
  rows: Record<string, any>[];
  totals?: Record<string, any>;
  generatedAt: number;
}

const qs = (q: Record<string, string | undefined>): string => {
  const s = new URLSearchParams(
    Object.entries(q).filter(([, v]) => v) as [string, string][],
  ).toString();
  return s ? `?${s}` : '';
};

export const inventoryApi = {
  // -- Vendors --------------------------------------------------------------
  listVendors: () => req<{ items: VendorRow[]; count: number }>('/vendors'),
  /**
   * There is no `GET /vendors/{id}` route — vendor detail is COMPOSED from the
   * two reads that do exist, matching the `{ vendor, grns }` shape callers use.
   */
  getVendor: async (vendorId: string): Promise<{ vendor: VendorRow; grns: GrnRow[] }> => {
    const [vs, gs] = await Promise.all([
      req<{ items: VendorRow[]; count: number }>('/vendors'),
      req<{ items: GrnRow[]; count: number }>(`/grn${qs({ vendorId })}`),
    ]);
    const vendor = (vs.items || []).find((v) => v.vendorId === vendorId);
    if (!vendor) throw new Error(`Vendor ${vendorId} not found`);
    return { vendor, grns: gs.items || [] };
  },
  upsertVendor: (vendor: Partial<VendorRow>) =>
    req<{ vendor: VendorRow }>('/vendors', { method: 'POST', body: JSON.stringify(vendor) }),

  // -- GRN (Goods Received) --------------------------------------------------
  /** `limit`/`nextToken` — B1 pagination contract; omit `limit` for the old, unpaginated response. */
  /**
   * `from`/`to` are YYYY-MM-DD days, filtered SERVER-side against `createdAt`
   * — `to` is inclusive of that whole day. Filtering here rather than in the
   * browser matters because the list is paginated: a client-side filter would
   * only ever narrow the 20 rows already fetched.
   */
  listGrns: (q: { vendorId?: string; from?: string; to?: string; limit?: number; nextToken?: string } = {}) =>
    req<{ items: GrnRow[]; count: number; nextToken?: string }>(`/grn${qs({ vendorId: q.vendorId, from: q.from, to: q.to, limit: q.limit ? String(q.limit) : undefined, nextToken: q.nextToken })}`),
  createGrn: (grn: { vendorId: string; poRef?: string; lines: GrnLineInput[]; expectedAt?: number }) =>
    req<{ grn: GrnRow; lines: GrnLineRow[] }>('/grn', { method: 'POST', body: JSON.stringify(grn) }),
  /**
   * Accept ONE unit off a GRN as it comes out of the box: mints its tracking
   * ID, lands it `in_stock`. A damaged arrival goes through the SAME call with
   * `damaged: true` (+ reason/chargedTo) — the server routes it to `rma` and
   * writes the damage row in one transaction, GRN attached.
   */
  receiveGrnUnit: (
    grnId: string,
    body: {
      modelId: string;
      serial?: string;
      damaged?: boolean;
      reason?: string;
      chargedTo?: 'vendor' | 'tenant' | 'writeoff';
      amountCents?: number;
      notes?: string;
    },
  ) =>
    req<{ unit: { unitId: string; trackingId?: string; [k: string]: any }; trackingId: string }>(
      `/grn/${encodeURIComponent(grnId)}/receive`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * The QUANTITY-mode receive path — same endpoint, branches per-line on the
   * line's model's `trackingMode` (mixed serialized+quantity GRNs are fine).
   * No unit minted: one transaction decrements the GRN line's `remainingQty`
   * and writes one `received` (+qty) movement. `qty` must not exceed what's
   * left on the line — the server 409s over-receiving with its own message.
   */
  receiveGrnBulk: (grnId: string, body: { modelId: string; qty: number }) =>
    req<{ movement: StockMovementRow }>(`/grn/${encodeURIComponent(grnId)}/receive`, {
      method: 'POST', body: JSON.stringify(body),
    }),

  // -- Damage / write-offs ---------------------------------------------------
  /** `limit`/`nextToken` — B1 pagination contract; omit `limit` for the old, unpaginated response. */
  listDamage: (q: { unitId?: string; stage?: string; chargedTo?: string; status?: DamageStatus; limit?: number; nextToken?: string } = {}) =>
    req<{ items: DamageRow[]; count: number; nextToken?: string }>(
      `/damage${qs({ unitId: q.unitId, stage: q.stage, chargedTo: q.chargedTo, status: q.status, limit: q.limit ? String(q.limit) : undefined, nextToken: q.nextToken })}`,
    ),
  logDamage: (body: {
    unitId: string;
    stage: string;
    reason: string;
    chargedTo: 'vendor' | 'tenant' | 'writeoff';
    amountCents?: number;
    notes?: string;
    state?: string;
  }) =>
    req<{ unit: Record<string, any>; damage: DamageRow }>('/damage', { method: 'POST', body: JSON.stringify(body) }),

  /**
   * Damage lifecycle wave — approve/cancel/return a damage report. Rides
   * hardware-service's `/lifecycle/{proxy+}` router (see hardwareApi.ts's
   * lifecycle routes); grouped here instead since it works with `DamageRow`
   * and pairs with `listDamage`/`logDamage` above. `createdAt` is the
   * report's own `createdAt` — the only thing besides `unitId` that names
   * one report unambiguously. Idempotent server-side: resolving an
   * already-closed report 409s (throws) rather than double-acting.
   */
  resolveDamage: (body: { unitId: string; createdAt: number; action: DamageResolveAction; notes?: string }) =>
    req<{ unit: Record<string, any>; damage: DamageRow; note?: string }>('/lifecycle/damage/resolve', {
      method: 'POST', body: JSON.stringify(body),
    }),

  // -- Stock (quantity-mode models) ------------------------------------------
  /** Batch on-hand/reserved/available for the Stock tab — ONE call per page of models, never per-row. Capped at 50 modelIds server-side. */
  stockSummary: (modelIds: string[]) =>
    req<Record<string, StockSummary>>(`/lifecycle/stock${qs({ modelIds: modelIds.join(',') })}`),
  /** Newest-first ledger for one model. Same `limit`/`nextToken` contract as every other list here — omit `limit` for the full, unpaginated history. */
  stockMovements: (modelId: string, q: { limit?: number; nextToken?: string } = {}) =>
    req<{ items: StockMovementRow[]; count: number; nextToken?: string }>(
      `/lifecycle/stock/${encodeURIComponent(modelId)}/movements${qs({ limit: q.limit ? String(q.limit) : undefined, nextToken: q.nextToken })}`,
    ),
  /**
   * Record a signed adjustment. Never refuses an adjustment that would take
   * on-hand negative — the response's `warning` says so instead, so a
   * recount is always recordable. `reason:'damaged'` requires `delta < 0`
   * and also returns the `damage` row it wrote (same loss report the Damage
   * tab reads).
   */
  adjustStock: (modelId: string, body: { delta: number; reason: StockAdjustReason; note?: string; chargedTo?: 'vendor' | 'tenant' | 'writeoff' }) =>
    req<{ movement: StockMovementRow; stock: StockSummary; damage?: DamageRow; warning?: string }>(
      `/lifecycle/stock/${encodeURIComponent(modelId)}/adjust`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  // -- Brands -----------------------------------------------------------------
  /**
   * Distinct `brand` strings across all device models. A brand is CREATED by
   * setting the `brand` field on a model (`POST /models`) — there is no
   * brand row and no POST /brands.
   */
  listBrands: () => req<{ items: string[]; count: number }>('/brands'),

  // -- Reports ------------------------------------------------------------
  /** Uniform `{ columns, rows, totals?, generatedAt }` for any report id. */
  report: (reportId: string, params: Record<string, string | undefined> = {}) =>
    req<ReportWire>(`/reports/${encodeURIComponent(reportId)}${qs(params)}`),
};
